import { z } from 'zod';
import { offerRequestAttachmentSchema } from '../../offer-request-management/attachment.js';
import { MAX_ENGAGEMENT_MESSAGE_LENGTH } from '../partner-matching/engagement.js';
import { MAX_ENGAGEMENT_CHAT_ATTACHMENTS } from './chatAttachmentLimits.js';
export const EngagementChatSenderType = {
    Customer: 'customer',
    PartnerCockpitUser: 'partner-cockpit-user',
    System: 'system',
};
export const engagementChatSenderTypeSchema = z.enum([
    EngagementChatSenderType.Customer,
    EngagementChatSenderType.PartnerCockpitUser,
    EngagementChatSenderType.System,
]);
export const EngagementChatAudience = {
    Customer: EngagementChatSenderType.Customer,
    PartnerCockpitUser: EngagementChatSenderType.PartnerCockpitUser,
};
export const engagementChatAudienceSchema = z.enum([
    EngagementChatAudience.Customer,
    EngagementChatAudience.PartnerCockpitUser,
]);
export const engagementChatMessageSenderSchema = z.object({
    type: engagementChatSenderTypeSchema,
    name: z.string(),
    // Whether the message was sent by the current chat viewer.
    isAuthor: z.boolean(),
});
export const customerEngagementChatSystemNoticeSchema = z.enum([
    'PartnerInterested',
    'PartnerWillContactViaPhoneOrEmail',
]);
export const partnerEngagementChatSystemNoticeSchema = z.enum([
    'ContactRequestSent',
    'ContactViaPhoneEmail',
    'ContactRequestAccepted',
]);
export const engagementChatSystemNoticeSchema = z.enum([
    ...customerEngagementChatSystemNoticeSchema.options,
    ...partnerEngagementChatSystemNoticeSchema.options,
]);
export const engagementChatSystemNoticesForAudience = {
    [EngagementChatAudience.Customer]: customerEngagementChatSystemNoticeSchema.options,
    [EngagementChatAudience.PartnerCockpitUser]: partnerEngagementChatSystemNoticeSchema.options,
};
export const engagementChatAttachmentSchema = offerRequestAttachmentSchema;
export const engagementChatMessageItemSchema = z.object({
    uuid: z.uuid(),
    createdAt: z.iso.datetime(),
    // TODO: Chat UIs render contentText only and we can support rich formatting in the future.
    contentDelta: z.unknown(),
    contentText: z.string(),
    variables: z.record(z.string(), z.unknown()).nullable().optional(),
    sender: engagementChatMessageSenderSchema,
    systemNotice: engagementChatSystemNoticeSchema.nullable(),
    attachments: z.array(engagementChatAttachmentSchema),
});
export const engagementChatMessagesPageSchema = z.object({
    items: z.array(engagementChatMessageItemSchema),
    // Absent when there are no older messages left.
    nextCursor: z.uuid().optional(),
    // Gates the composer only — a closed chat keeps its history readable.
    canSendMessages: z.boolean(),
});
export const engagementChatMessagesQuerySchema = z.object({
    // Uuid of the oldest already-loaded message
    cursor: z.uuid().optional(),
    pageSize: z.coerce.number().int().min(1).max(50).optional(),
});
export const engagementChatUnseenCountSchema = z.object({
    count: z.number().int().min(0),
});
export const engagementChatUnseenCountQuerySchema = z.object({
    // Uuid of the newest message the viewer has loaded
    lastSeenMessageUuid: z.uuid().optional(),
});
export const markEngagementChatSeenSchema = z.object({
    lastSeenMessageUuid: z.uuid(),
});
// Bounds keep a chat message's rich-text delta from bloating: total inserted text is capped at the shared
// engagement message length, and MAX_OPS caps the number of delta ops.
const MAX_ENGAGEMENT_CHAT_MESSAGE_OPS = 4000;
export const quillDeltaOpSchema = z
    .looseObject({
    insert: z.union([z.string().max(MAX_ENGAGEMENT_MESSAGE_LENGTH), z.record(z.string(), z.unknown())]).optional(),
    delete: z.number().optional(),
    retain: z.union([z.number(), z.record(z.string(), z.unknown())]).optional(),
    attributes: z.record(z.string(), z.unknown()).nullish(),
})
    .refine((op) => op.insert !== undefined || op.delete !== undefined || op.retain !== undefined, {
    message: 'A delta op needs one of insert, delete or retain',
});
const quillDeltaOpsSchema = z.array(quillDeltaOpSchema).max(MAX_ENGAGEMENT_CHAT_MESSAGE_OPS);
function quillDeltaOps(delta) {
    return Array.isArray(delta) ? delta : delta.ops;
}
function totalInsertedTextLength(ops) {
    return ops.reduce((total, op) => total + (typeof op.insert === 'string' ? op.insert.length : 0), 0);
}
export const quillDeltaSchema = z
    .union([quillDeltaOpsSchema, z.object({ ops: quillDeltaOpsSchema })])
    .refine((delta) => totalInsertedTextLength(quillDeltaOps(delta)) <= MAX_ENGAGEMENT_MESSAGE_LENGTH, {
    message: `Message content exceeds ${MAX_ENGAGEMENT_MESSAGE_LENGTH} characters`,
});
function isEmptyDelta(delta) {
    const ops = quillDeltaOps(delta);
    if (ops.length === 0) {
        return true;
    }
    return ops.length === 1 && (!ops[0]?.insert || ops[0].insert === '\n');
}
export const postEngagementChatMessageSchema = z
    .object({
    content: quillDeltaSchema.optional(),
    attachmentUuids: z.array(z.uuid()).max(MAX_ENGAGEMENT_CHAT_ATTACHMENTS).optional(),
})
    .superRefine((input, ctx) => {
    const hasContent = input.content !== undefined && !isEmptyDelta(input.content);
    const hasAttachments = (input.attachmentUuids?.length ?? 0) > 0;
    if (!hasContent && !hasAttachments) {
        ctx.addIssue({
            code: 'custom',
            message: 'Message must include content or attachments',
            path: ['content'],
        });
    }
    if (input.attachmentUuids !== undefined &&
        new Set(input.attachmentUuids).size !== input.attachmentUuids.length) {
        ctx.addIssue({
            code: 'custom',
            message: 'Duplicate attachment UUIDs are not allowed',
            path: ['attachmentUuids'],
        });
    }
});
export const engagementChatPostedMessageSchema = z.object({
    uuid: z.uuid(),
});
